import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, Calculator, Layers, FileText, Wallet, FileSignature, AlertTriangle } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { ESS_COLORS } from './constants';
import { useDOSMVP } from './useDOSMVP';
import SimulateurTab from './SimulateurTab';
import ProgrammesTab from './ProgrammesTab';
import { HistoriqueTab, FondsTab, SignatureTab } from './DOSMVPExtraTabs';

export default function DOSMVPSimulateur({ userRole = 'super_admin' }) {
  const ctx = useDOSMVP(userRole);
  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  const { activeTab, setActiveTab, showAttestationModal, setShowAttestationModal, attestationHtml } = ctx;

  return (
    <div className="space-y-6" data-testid="dos-mvp-simulateur">
      {/* En-tête institutionnel COPPAM — style "Mutuelle Premium" */}
      <div className="relative overflow-hidden rounded-2xl text-white shadow-2xl border border-white/10"
           style={{ background: `radial-gradient(circle at 25% 0%, rgba(255,255,255,0.10), transparent 55%), linear-gradient(135deg, #0B3C5D 0%, #0A7F5A 100%)` }}>
        {/* Motif décoratif */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-emerald-200 rounded-full blur-3xl translate-y-24 -translate-x-24" />
        </div>
        <div className="relative px-6 sm:px-8 py-7">
          {/* Filet supérieur officiel */}
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-white/85 mb-3">
            <span className="inline-block w-7 h-px bg-amber-300/80" />
            <span className="font-semibold">COPPAM</span>
            <span className="text-white/40">|</span>
            <span>Garanties Solidaires</span>
            <span className="text-white/40">|</span>
            <span>Dispositif certifié eIDAS</span>
          </div>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-5 flex-1 min-w-[280px]">
              {/* Sceau institutionnel */}
              <div className="hidden sm:flex relative flex-shrink-0">
                <div className="absolute inset-0 bg-amber-300/20 rounded-2xl blur-xl" />
                <div className="relative inline-flex items-center justify-center w-[68px] h-[68px] rounded-2xl bg-white/12 backdrop-blur-md border border-white/25 shadow-xl">
                  <Shield className="h-9 w-9 drop-shadow-lg" />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="text-[clamp(1.75rem,3.2vw,2.5rem)] leading-[1.05] font-black tracking-tight" style={{ fontFamily: '"Playfair Display", "Georgia", serif' }}>
                  DOS&nbsp;Solidaire
                </h1>
                <p className="mt-2 text-[15px] text-white/85 font-light max-w-2xl">
                  Dommages-Ouvrage&nbsp;Solidaire — Couverture mutualisée des risques d'ouvrage en Outre-mer
                </p>
                <p className="mt-2 text-[11px] tracking-wide text-amber-200/90 font-medium">
                  Guadeloupe&nbsp;•&nbsp;Mayotte&nbsp;•&nbsp;La&nbsp;Réunion — Extension Martinique, Guyane, Saint-Martin, Saint-Barthélemy
                </p>
              </div>
            </div>
            {/* Badges institutionnels */}
            <div className="flex flex-col gap-2 items-end flex-shrink-0">
              <Badge className="text-[11px] px-3 py-1.5 font-semibold border border-amber-200/40 bg-white text-slate-800/10 backdrop-blur-md text-amber-100 hover:bg-white/15">
                <Shield className="h-3 w-3 mr-1.5" /> eIDAS SEA — Niveau qualifié
              </Badge>
              <div className="flex gap-2">
                <Badge className="text-[11px] px-2.5 py-1 font-semibold border border-white/25 bg-white text-slate-800/10 backdrop-blur-md hover:bg-white/15">
                  Multi-lots
                </Badge>
                <Badge className="text-[11px] px-2.5 py-1 font-semibold border border-white/25 bg-white text-slate-800/10 backdrop-blur-md hover:bg-white/15">
                  Décennale
                </Badge>
              </div>
            </div>
          </div>
          {/* Filet inférieur fin doré */}
          <div className="mt-6 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
        </div>
      </div>
      
      {/* Mention juridique */}
      <div className="border rounded-xl p-4" style={{ backgroundColor: ESS_COLORS.sableClair, borderColor: '#D1D5DB' }}>
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: ESS_COLORS.statutSuspendu }} />
          <div className="text-sm" style={{ color: ESS_COLORS.grisStructurant }}>
            <strong>Mention juridique :</strong> Ce dispositif constitue un mécanisme solidaire de 
            mutualisation des risques de nature civile, fondé sur l&apos;adhésion volontaire. 
            <strong> Il ne constitue ni une assurance, ni une garantie automatique au sens du Code des assurances.</strong>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 gap-2 h-auto p-1">
          <TabsTrigger value="simulateur" className="py-3" data-testid="tab-simulateur">
            <Calculator className="h-4 w-4 mr-2" />
            Simulateur
          </TabsTrigger>
          <TabsTrigger value="programmes" className="py-3" data-testid="tab-programmes">
            <Layers className="h-4 w-4 mr-2" />
            Programmes
          </TabsTrigger>
          <TabsTrigger value="historique" className="py-3" data-testid="tab-historique">
            <FileText className="h-4 w-4 mr-2" />
            Historique
          </TabsTrigger>
          {(userRole === 'super_admin' || userRole === 'admin') && (
            <TabsTrigger value="fonds" className="py-3" data-testid="tab-fonds">
              <Wallet className="h-4 w-4 mr-2" />
              Fonds
            </TabsTrigger>
          )}
          {(userRole === 'super_admin' || userRole === 'admin') && (
            <TabsTrigger value="signature" className="py-3" data-testid="tab-signature">
              <FileSignature className="h-4 w-4 mr-2" />
              Signature
            </TabsTrigger>
          )}
        </TabsList>

        <SimulateurTab ctx={ctx} />
        <ProgrammesTab ctx={ctx} />
        <HistoriqueTab ctx={ctx} />
        {isAdmin && <FondsTab ctx={ctx} />}
        {isAdmin && <SignatureTab ctx={ctx} />}
      </Tabs>

      <Dialog open={showAttestationModal} onOpenChange={setShowAttestationModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Attestation DOS</DialogTitle>
            <DialogDescription>
              Aperçu de l&apos;attestation Dommage-Ouvrage Solidaire
            </DialogDescription>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(attestationHtml) }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttestationModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
